import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  HeadingLevel,
  LineRuleType,
} from "docx";
import type { DocState, SongChoice } from "../state";
import { hasGloria } from "../state";
import { formatFrLong } from "./date";

const FONT = "Calibri";

// Single line spacing (240 = 1.0), no extra before-spacing — forces tight,
// "normal text" lines regardless of the viewer's default (Word/Pages otherwise
// render paragraphs with no explicit line spacing too airily).
const tight = (after = 0) => ({ before: 0, after, line: 240, lineRule: LineRuleType.AUTO });

function p(children: TextRun[] | TextRun, opts: any = {}): Paragraph {
  const { spacing, ...rest } = opts;
  return new Paragraph({
    children: Array.isArray(children) ? children : [children],
    spacing: { after: 120, ...(spacing || {}) },
    ...rest,
  });
}

function run(text: string, opts: any = {}): TextRun {
  return new TextRun({ text, font: FONT, size: 22, ...opts });
}

/** "Label : value" with the label in bold. */
function labeled(label: string, value: string): Paragraph {
  return p([run(`${label} : `, { bold: true }), run(value || "—")]);
}

/** A song line: "Chant d'entrée : Title (feuille)" or "… (page 1042)". */
function songLine(label: string, c: SongChoice): Paragraph {
  const runs = [run(`${label} : `, { bold: true }), run(c.title || "—")];
  let paren = "";
  if (c.annotation === "page" || c.annotation === "carnet") {
    const pg = (c.page || "").trim().replace(/^(carnet\s+)?p(age)?\.?\s*/i, "");
    const lead = c.annotation === "carnet" ? "carnet p." : "page";
    paren = pg ? `${lead} ${pg}` : c.annotation;
  } else if (c.annotation) {
    paren = c.annotation;
  }
  if (paren) runs.push(run(`  (${paren})`, { italics: true }));
  return p(runs);
}

/** Animateur block: bold heading + the spoken text. */
function animateur(title: string, text: string): Paragraph[] {
  return [
    p(run(`Animateur — ${title}`, { bold: true, color: "1F6FEB" }), {
      spacing: { before: 120, after: 40 },
    }),
    ...multiline(text || "…"),
  ];
}

/** Split multi-line text into one Paragraph per line. */
function multiline(text: string, opts: any = {}): Paragraph[] {
  const lines = (text || "").split("\n");
  if (lines.length === 0) return [p(run(""))];
  return lines.map((line, i) =>
    p(run(line), { spacing: { after: i === lines.length - 1 ? 120 : 0 } , ...opts })
  );
}

/** Psalm verses grouped into stanzas of 4 lines (a blank line between groups);
 *  drops empty lines and lone "R/" refrain markers. */
function psalmGrouped(text: string): Paragraph[] {
  const lines = (text || "").split("\n").map((x) => x.trim()).filter((x) => x && !/^r\s*[/.]?$/i.test(x));
  const out: Paragraph[] = [];
  lines.forEach((line, i) => {
    const last = i === lines.length - 1;
    out.push(p(run(line), { spacing: tight(last ? 120 : 0) }));
    if (!last && (i + 1) % 4 === 0) out.push(p(run(""), { spacing: tight(0) }));
  });
  return out;
}

function heading(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 200, after: 80 },
    children: [run(text, { bold: true, size: 24 })],
  });
}

export function buildDocument(s: DocState): Document {
  const l = s.liturgy;
  const body: Paragraph[] = [];

  // ---- Title ----
  body.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
      children: [run(formatFrLong(s.date), { bold: true, size: 30 })],
    })
  );
  body.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 20 },
      children: [run(l.dayName, { size: 24 })],
    })
  );
  if (l.year)
    body.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [run(`Année ${l.year}${l.color ? ` — ${l.color}` : ""}`, { italics: true })],
      })
    );

  // ---- Accueil ----
  body.push(...animateur("Mot d'accueil", s.motAccueil));
  body.push(songLine("Chant d'entrée", s.entree));

  // ---- Rite pénitentiel & ordinaire ----
  body.push(labeled("Préparation pénitentielle", "Je confesse à Dieu — récité"));
  body.push(labeled("Kyrie", s.ordinary));
  if (hasGloria(l.seasonKey)) body.push(labeled("Gloria", s.ordinary));
  if (s.oraisonOuverture) body.push(labeled("Prière d'ouverture", s.oraisonOuverture));

  // ---- Liturgie de la Parole ----
  body.push(heading("Liturgie de la Parole"));
  if (l.first) {
    body.push(...animateur("Avant la première lecture", s.avant1));
    body.push(labeled("Première lecture", `${l.first.intro || ""} (${l.first.ref})`.trim()));
    if (l.first.title) body.push(p(run(l.first.title, { italics: true })));
    if (s.includeReadingsText && l.first.text) body.push(...multiline(l.first.text));
  }
  if (l.psalm) {
    body.push(p([run(`Psaume ${l.psalm.ref} : `, { bold: true }), run("—")], { spacing: tight(40) }));
    if (s.psalmRefrain) body.push(p([run("Refrain : ", { bold: true }), run(s.psalmRefrain, { bold: true })], { spacing: tight(60) }));
    if (l.psalm.text) body.push(...psalmGrouped(l.psalm.text));
  }
  if (l.second) {
    body.push(...animateur("Avant la deuxième lecture", s.avant2));
    body.push(labeled("Deuxième lecture", `${l.second.intro || ""} (${l.second.ref})`.trim()));
    if (l.second.title) body.push(p(run(l.second.title, { italics: true })));
    if (s.includeReadingsText && l.second.text) body.push(...multiline(l.second.text));
  }
  if (l.acclamation) {
    body.push(labeled("Acclamation à l'Évangile", ""));
    body.push(...multiline(l.acclamation.text));
  }
  if (l.gospel) {
    body.push(labeled("Évangile", `${l.gospel.intro || ""} (${l.gospel.ref})`.trim()));
    if (l.gospel.title) body.push(p(run(l.gospel.title, { italics: true })));
    if (s.includeReadingsText && l.gospel.text) body.push(...multiline(l.gospel.text));
  }
  body.push(labeled("Homélie", ""));
  body.push(labeled("Profession de foi", "Credo récité"));

  // ---- Prière universelle ----
  body.push(heading("Prière universelle"));
  if (s.prierePuRefrain)
    body.push(p([run("Refrain : ", { bold: true }), run(s.prierePuRefrain)]));
  for (const intention of s.prierePuIntentions.split("\n").filter((x) => x.trim())) {
    body.push(p([run("— ", { bold: true }), run(intention.trim())]));
  }

  // ---- Liturgie eucharistique ----
  body.push(heading("Liturgie eucharistique"));
  body.push(songLine("Chant d'offertoire", s.offertoire));
  if (s.oraisonOffertoire) body.push(labeled("Prière sur les offrandes", s.oraisonOffertoire));
  body.push(labeled("Sanctus", s.ordinary));
  body.push(labeled("Anamnèse", s.ordinary));
  body.push(labeled("Notre Père", "récité"));
  body.push(labeled("Agneau de Dieu", s.ordinary));
  body.push(songLine("Communion", s.communion));
  if (s.oraisonCommunion) body.push(labeled("Prière après la communion", s.oraisonCommunion));

  // ---- Envoi ----
  body.push(heading("Envoi"));
  body.push(labeled("Bénédiction", "Le prêtre"));
  body.push(labeled("Annonces", ""));
  body.push(songLine("Chant d'envoi", s.envoi));

  return new Document({
    creator: "Animation de messe",
    title: `Messe ${s.date}`,
    styles: {
      default: {
        document: { run: { font: FONT, size: 22 } },
      },
    },
    sections: [{ properties: {}, children: body }],
  });
}

/** Correct MIME for a .docx — Packer.toBlob() returns application/zip, which some
 *  share targets (WhatsApp) reject; we re-wrap with this so the file is recognised. */
export const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** The feuille animateur filename for a given mass. */
export function docxFilename(s: DocState): string {
  return `${s.date}_messe.docx`;
}

/** Render the feuille animateur to a .docx Blob (reused by download + WhatsApp share). */
export async function docxBlob(s: DocState): Promise<Blob> {
  const raw = await Packer.toBlob(buildDocument(s));
  return raw.type === DOCX_MIME ? raw : new Blob([raw], { type: DOCX_MIME });
}

export async function downloadDocx(s: DocState): Promise<void> {
  const blob = await docxBlob(s);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = docxFilename(s);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
